import React, { useEffect, useState, useRef } from "react";
import {
  Layout,
  Menu,
  Button,
  Typography,
  Dropdown,
  Space,
  message,
} from "antd";
import {
  PhoneOutlined,
  LogoutOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  DownOutlined,
  UpOutlined,
  InfoCircleOutlined,
  HomeOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  LeftOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { getUserName } from "../utils/tokenUtils";
import { showCallOptionsModal } from "../utils/callUtils";
import { APP_CONFIG } from "../utils/constants";
import "../assets/styles/mainLayout.css";
import { createUserNotificationToken } from "../services/firebase-messaging";
import OnlineStatus from "../components/OnlineStatus";
import CallMeConfirmModal from "../components/CallMeConfirmModal";
import PanicConfirmModal from "../components/PanicConfirmModal";
import {
  getCallMeId as getCallMeIdCookie,
  clearCallMeId as clearCallMeIdCookie,
  getPanicId as getPanicIdCookie,
  clearPanicId as clearPanicIdCookie,
  getUserDetails as getUserDetailsCookie,
} from "../utils/cookieUtils";
import { getCallMeById, getPanicById } from "../utils/apiService";

import { branding } from "../config/branding";

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = getUserName();
  const [pageTitle, setPageTitle] = useState("");
  const [showBackButton, setShowBackButton] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const contentRef = useRef(null);
  const fcmInitCalledRef = useRef(false);
  // Bottom-nav actions state (mirrors Dashboard behavior)
  const [callMeModalVisible, setCallMeModalVisible] = useState(false);
  const [callMeId, setCallMeIdState] = useState(null);
  const [panicModalVisible, setPanicModalVisible] = useState(false);
  const [panicId, setPanicIdState] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [registerSecura, setRegisterSecura] = useState(null);

  useEffect(() => {
    if (fcmInitCalledRef.current) return;
    fcmInitCalledRef.current = true;

    // Only try to get token if notifications are supported and permission is granted
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "granted") {
        Promise.resolve(createUserNotificationToken()).catch((err) => {
          console.warn("[FCM] Initialization error:", err);
        });
      } else if (Notification.permission !== "denied") {
        // If permission hasn't been requested yet, we'll request it on user interaction
        console.log("[FCM] Notification permission not granted yet");
      }
    } else {
      console.log("[FCM] Notifications not supported in this browser");
    }
  }, []);

  // Update page title based on route
  useEffect(() => {
    if (location.pathname === "/events/new") {
      setPageTitle("New Event");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/events/")) {
      setPageTitle("Event Details");
      setShowBackButton(true);
    } else if (location.pathname.includes("/breakdown/")) {
      setPageTitle("Details");
      setShowBackButton(true);
    } else if (location.pathname === "/dashboard") {
      setPageTitle("Dashboard");
      setShowBackButton(false);
    } else if (location.pathname === "/breakdowns") {
      setPageTitle("Breakdowns");
      setShowBackButton(true);
    } else if (location.pathname === "/events") {
      setPageTitle("Events");
      setShowBackButton(true);
    } else if (location.pathname === "/profile") {
      setPageTitle("Profile");
      setShowBackButton(true);
    } else if (location.pathname === "/createBreakdown") {
      setPageTitle("New Breakdown");
      setShowBackButton(true);
    } else if (location.pathname === "/breakdownRequests") {
      setPageTitle("Breakdown Requests");
      setShowBackButton(true);
    } else if (location.pathname === "/accidentReports") {
      setPageTitle("Accident Reports");
      setShowBackButton(true);
    } else if (location.pathname === "/accidentReports/drafts") {
      setPageTitle("Accident Report Drafts");
      setShowBackButton(true);
    } else if (location.pathname === "/accidentReports/new") {
      setPageTitle("New Accident Report");
      setShowBackButton(true);
    } else if (location.pathname === "/about") {
      setPageTitle("About");
      setShowBackButton(true);
    } else if (location.pathname === "/inspections") {
      setPageTitle("Inspections");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/inspections/templates/")) {
      setPageTitle("New Inspection");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/inspections/responses/")) {
      setPageTitle("Inspection Draft");
      setShowBackButton(true);
    } else if (location.pathname === "/inspections/new") {
      setPageTitle("New Inspections");
      setShowBackButton(true);
    } else if (location.pathname === "/inspections/new/in-progress") {
      setPageTitle("In Progress");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/inspections/newInspections")) {
      setPageTitle("New Inspection");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/inspections/draftInspections")) {
      setPageTitle("Draft Inspections");
      setShowBackButton(true);
    } else if (location.pathname === "/documents") {
      setPageTitle("Documents");
      setShowBackButton(true);
    } else if (location.pathname === "/documents/in-progress") {
      setPageTitle("In Progress");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/documents/fill/")) {
      setPageTitle(location.state?.pageTitle ?? "New Document");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/documents/")) {
      setPageTitle(location.state?.pageTitle ?? "Document");
      setShowBackButton(true);
    } else if (location.pathname.startsWith("/map")) {
      setPageTitle("Map");
      setShowBackButton(true);
    } else {
      setPageTitle("");
      setShowBackButton(false);
    }
  }, [location.pathname, location.state]);

  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      // Show button when user scrolls down 100px from the top
      if (contentElement.scrollTop > 100) {
        setShowScrollTopButton(true);
      } else {
        setShowScrollTopButton(false);
      }
    };

    // Add scroll event listener to the content container
    contentElement.addEventListener("scroll", handleScroll);

    // Clean up event listener
    return () => contentElement.removeEventListener("scroll", handleScroll);
  }, [contentRef.current]); // Re-attach if ref changes

  // Refresh token if user is active
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      authService.refreshTokenIfNeeded();
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  // Restore Call Me and Panic state from cookies
  useEffect(() => {
    const existingCallMeId = getCallMeIdCookie();
    if (existingCallMeId) setCallMeIdState(existingCallMeId);
    const existingPanicId = getPanicIdCookie();
    if (existingPanicId) setPanicIdState(existingPanicId);
  }, []);

  // Load user details from cookie (used to gate Panic)
  useEffect(() => {
    const u = getUserDetailsCookie();
    if (u) {
      setUserDetails(u);
      setRegisterSecura(u.registerSecura);
    }
  }, []);

  // Poll Call Me status while in progress
  useEffect(() => {
    if (!callMeId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCallMeById(callMeId);
        const status = data?.pleaseCallStatus;
        if ([2, 3, 4].includes(status)) {
          if (!cancelled) {
            clearCallMeIdCookie();
            setCallMeIdState(null);
            message.success("Call Me completed. You can request again.");
          }
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) {
            clearCallMeIdCookie();
            setCallMeIdState(null);
          }
        } else {
          console.error("GetCallMeById failed", err);
        }
      }
    };

    // initial poll and interval
    poll();
    const timer = setInterval(poll, APP_CONFIG.CALL_ME_REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [callMeId]);

  // Poll Panic status while in progress
  useEffect(() => {
    if (!panicId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getPanicById(panicId);
        const status = data?.panicStatus;
        if ([2, 3, 4].includes(status)) {
          if (!cancelled) {
            clearPanicIdCookie();
            setPanicIdState(null);
            message.success("Panic completed. You can send another if needed.");
          }
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!cancelled) {
            clearPanicIdCookie();
            setPanicIdState(null);
          }
        } else {
          console.error("GetPanicById failed", err);
        }
      }
    };

    // initial poll and interval
    poll();
    const timer = setInterval(poll, APP_CONFIG.PANIC_REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [panicId]);

  // Handle logout
  const handleLogout = () => {
    authService.logout();
  };

  // Handle back navigation
  const handleBack = () => {
    // If in breakdowns page, go back to dashboard
    if (location.pathname.startsWith("/events/")) {
      navigate("/events");
    } else if (location.pathname === "/events") {
      navigate("/dashboard");
    } else if (location.pathname === "/breakdowns") {
      navigate("/dashboard");
    } else if (location.pathname === "/profile") {
      navigate("/dashboard");
    } else if (location.pathname === "/breakdownRequests") {
      navigate("/dashboard");
    } else if (location.pathname === "/accidentReports") {
      navigate("/dashboard");
    } else if (location.pathname === "/accidentReports/drafts") {
      navigate("/accidentReports");
    } else if (location.pathname === "/accidentReports/new") {
      navigate("/accidentReports");
    } else if (location.pathname === "/about") {
      navigate("/dashboard");
    } else if (location.pathname === "/inspections") {
      navigate("/dashboard");
    } else if (location.pathname === "/inspections/newInspections") {
      navigate("/inspections");
    } else if (location.pathname === "/inspections/draftInspections") {
      navigate("/inspections");
    } else if (location.pathname === "/inspections/new") {
      navigate("/dashboard");
    } else if (location.pathname.startsWith("/inspections/new/")) {
      navigate("/inspections/new");
    } else if (location.pathname === "/documents") {
      navigate("/dashboard");
    } else if (location.pathname.startsWith("/documents/")) {
      // Both the fill document page and an opened document belong to the library.
      navigate(
        location.state?.returnTo ??
          (location.pathname === "/documents/in-progress" ? "/documents" : "/documents/in-progress")
      );
    } else if (location.pathname.includes("/notes")) {
      navigate("/dashboard");
    } else {
      navigate(-1);
    }
  };

  // User menu items for dropdown
  const userMenuItems = [
    {
      key: "profile",
      label: "My Profile",
      icon: <UserOutlined />,
      onClick: () => navigate("/profile"),
    },
    {
      key: "about",
      label: "About",
      icon: <InfoCircleOutlined />,
      onClick: () => navigate("/about"),
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  // Handle phone call with multiple options
  const handleCallRNR = () => {
    showCallOptionsModal(APP_CONFIG.CALL_NUMBER, "RNR Support");
  };

  // Handle scroll to top
  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Custom composite icons built with CSS for reliable alignment in Menu.Item
  const CallMeIcon = () => (
    <span className="composite-icon">
      <PhoneOutlined className="base" />
      <RightOutlined className="badge-left" />
    </span>
  );

  const CallRNRIcon = () => (
    <span className="composite-icon">
      <PhoneOutlined className="base" />
      <LeftOutlined className="badge-right" />
    </span>
  );

  // Bottom nav click handlers mirroring Dashboard logic
  const handleCallMeClick = () => {
    if (callMeId) return; // busy, ignore
    setCallMeModalVisible(true);
  };

  const handlePanicClick = () => {
    const panicBusy = !!panicId;
    const hasUserDetails =
      userDetails &&
      userDetails.registerSecura !== undefined &&
      userDetails.registerSecura !== null;
    const panicAllowed =
      !hasUserDetails || Number(userDetails.registerSecura) === 1;
    if (panicBusy) return;
    if (hasUserDetails && !panicAllowed) {
      message.warning("Please contact your supervisor to enable this service");
      return;
    }
    setPanicModalVisible(true);
  };

  return (
    <Layout className="main-layout">
      {/* Top Navigation */}
      <Header className="top-nav">
        <div className="top-nav-content">
          {/* Left side - Page title and back button */}
          <div className="page-header">
            {showBackButton && (
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                className="back-btn"
              />
            )}
            <img
              src={branding.logoSrc}
              alt={branding.logoAlt}
              style={{ height: 32, marginRight: 8, verticalAlign: "middle" }}
            />
            <Text className="page-title">{pageTitle}</Text>
            <OnlineStatus />
          </div>
          {/* Right side - User dropdown */}
          <div className="user-info">
            <Dropdown
              menu={{ items: userMenuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <a
                onClick={(e) => e.preventDefault()}
                className="user-dropdown-trigger"
              >
                <Space style={{ maxWidth: "120px" }}>
                  <div className="user-name">
                    {userName?.split("(")[0]?.trim()}
                  </div>
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </div>
        </div>
      </Header>

      {/* Main Content */}
      <Content className="main-content" ref={contentRef}>
        <Outlet />
        {showScrollTopButton && (
          <Button
            className="scroll-to-top-btn"
            type="primary"
            shape="circle"
            icon={<UpOutlined />}
            onClick={scrollToTop}
            aria-label="Scroll to top"
          />
        )}
      </Content>

      {/* Action Modals replicated from Dashboard */}
      <CallMeConfirmModal
        visible={callMeModalVisible}
        onCancel={() => setCallMeModalVisible(false)}
        onSubmitted={(id) => {
          setCallMeIdState(id);
          if (id != 'UAT') {
            message.success('Call Me submitted');
          }
          else {
            message.warning('Call Me unavailable on UAT');
          }
          setCallMeModalVisible(false);
        }}
      />

      <PanicConfirmModal
        visible={panicModalVisible}
        onCancel={() => setPanicModalVisible(false)}
        onSubmitted={(id) => {
          setPanicIdState(id);
          message.success("Panic sent");
          setPanicModalVisible(false);
        }}
      />

      {/* Bottom Navigation */}
      <Footer className="bottom-nav">
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          className="bottom-menu"
          disabledOverflow
          items={[
            {
              key: "/dashboard",
              icon: <HomeOutlined />,
              label: "Home",
              onClick: () => navigate("/dashboard"),
            },
            {
              key: "call-rnr",
              icon: <CallRNRIcon />,
              label: "Call RNR",
              onClick: handleCallRNR,
            },
            {
              key: "call-me",
              icon: <CallMeIcon />,
              label: callMeId ? (
                <span>
                  <LoadingOutlined style={{ marginRight: 6 }} />
                  Call Me…
                </span>
              ) : (
                "Call Me"
              ),
              onClick: handleCallMeClick,
              disabled: !!callMeId,
            },
            {
              key: "panic",
              icon: <ExclamationCircleOutlined />,
              label: panicId ? (
                <span>
                  <LoadingOutlined style={{ marginRight: 6 }} />
                  Panic…
                </span>
              ) : (
                "Panic"
              ),
              danger: true,
              onClick: handlePanicClick,
              disabled: !!panicId || registerSecura !== 1,
            },
          ]}
        />
      </Footer>
    </Layout>
  );
};

export default MainLayout;
